import React from "react";
import withRouter, { RouterProps } from "app/hoc/withRouter";
import { Button } from "component-library";

import {
  CONTACT_NEW_FULL_PATH,
  PROFILE_CONTACT_FULL_PATH,
} from "app/config/routes";
import { FullPageSpinner } from "shared/modules/Common/FullPageSpinner";
import {
  IGlobalContextProps,
  withGlobalContext,
} from "shared/contexts/GlobalContext";
import {
  AddAgencyForm,
  IAddAgencyFormContent,
  IAddAgencyFormContentKeys,
} from "./components/AddAgencyForm";
import {
  IAddAgencyPageContent,
  getCancelDialogPopup,
  ICancelAddingPopupContent,
  IAddAgencyPageContentKeys,
  ICancelAddingPopupContentKeys,
} from "./AddAgencyPageContent";
import {
  ITranslationContextProps,
  Translator,
  withTranslationF,
} from "contexts/TranslationContext";

import { GlobalEventTypes } from "contexts/GlobalContext";
import { ICancelablePromise, makeCancelable } from "shared/utils/promise";
import compose from "shared/utils/compose";
import { scrollToElement } from "shared/utils/dom";
import withBreadcrumbs from "app/hoc/withBreadcrumbs";
import { PageWrapper } from "components/common/PageWrapper";
import AgencyService from "services/AgencyService";
import { IAgency } from "models";

import "./AddAgencyPage.scss";
import {
  captureExceptionEvent,
  withLaunchDarklyConsumerHOC,
  LaunchDarklyClientWithFlags,
  FEATURE_FLAGS,
} from "shared-services";
import { heapTrackingForAddAgencyPage } from "app/helpers/heapTracking";

export interface IAddAgencyPageProps
  extends
    ITranslationContextProps,
    RouterProps,
    IGlobalContextProps,
    LaunchDarklyClientWithFlags {}

interface IAddAgencyPageState {
  content: IAddAgencyPageContent;
  agencies: IAgency[];
  selectedAgency?: IAgency;
  isAgencyFormInvalid: boolean;
  showLoader: boolean;
}

export class AddAgencyPage extends React.Component<
  IAddAgencyPageProps,
  IAddAgencyPageState
> {
  private agenciesPromise: ICancelablePromise<IAgency[]>;
  private isProcessStarted = false;

  constructor(props: Readonly<IAddAgencyPageProps>) {
    super(props);

    const content = AddAgencyPage.getTranslation(props.translator);

    this.state = {
      content,
      showLoader: true,
      isAgencyFormInvalid: false,
      agencies: [],
    };
  }

  public render() {
    const { content, isAgencyFormInvalid, showLoader, agencies } = this.state;

    return (
      <PageWrapper
        header={content.header}
        styles={{
          noMinHeight: true,
        }}
        buttons={[
          {
            text: content.close,
            onClick: this.tryCloseForm,
          },
        ]}
      >
        <AddAgencyForm
          content={content.formContent}
          agencies={agencies}
          selectAgency={this.selectAgency}
          addNewAgency={this.addNewAgency}
          isAgencyFormInvalid={isAgencyFormInvalid}
        />
        {this.renderFormButtons(content.cancel, content.addAgency)}
        {showLoader && <FullPageSpinner />}
      </PageWrapper>
    );
  }

  public async componentDidMount() {
    try {
      this.agenciesPromise = makeCancelable(AgencyService.searchAgencies());
      const agencies = await this.agenciesPromise.promise;

      this.setState({ agencies });
    } catch (error) {
      captureExceptionEvent(error);
    } finally {
      this.setState({ showLoader: false });
    }
  }

  public componentWillUnmount() {
    if (this.agenciesPromise) {
      this.agenciesPromise.cancel();
    }
  }

  public static getDerivedStateFromProps(
    nextProps: IAddAgencyPageProps,
    prevState: IAddAgencyPageState
  ) {
    let stateUpdates = {};

    if (
      nextProps.translator.version !==
      nextProps.translator.getTranslationVersion(prevState.content)
    ) {
      const content = AddAgencyPage.getTranslation(nextProps.translator);

      stateUpdates = { content };
    }

    return stateUpdates;
  }

  private renderFormButtons = (cancelText: string, addAgencyText: string) => {
    const { selectedAgency } = this.state;
    const { flags } = this.props;
    const isProfileRedesignEnabled = flags[FEATURE_FLAGS.ProfileRedesign];

    return (
      <div className="p-add-agency-page__buttons">
        <div className="p-add-agency-page__button">
          <Button
            type="primary"
            text={addAgencyText}
            disabled={!selectedAgency}
            onClick={async () => {
              heapTrackingForAddAgencyPage(isProfileRedesignEnabled);

              await this.addAgency();
            }}
          />
        </div>
        <div className="p-add-agency-page__button">
          <Button
            type="secondary"
            text={cancelText}
            onClick={this.tryCloseForm}
          />
        </div>
      </div>
    );
  };

  private selectAgency = (agency: IAgency) =>
    this.setState({ selectedAgency: agency, isAgencyFormInvalid: false });

  private addAgency = async () => {
    const { selectedAgency } = this.state;

    if (!this.isProcessStarted) {
      this.isProcessStarted = true;

      if (selectedAgency) {
        this.setState({ showLoader: true });

        try {
          await AgencyService.addAgencyForUser(selectedAgency);

          this.returnToAgenciesList();
        } catch (error) {
          captureExceptionEvent(error);
          this.setState({ showLoader: false });
        }
      } else {
        this.setState({ isAgencyFormInvalid: true });
      }

      this.isProcessStarted = false;
    }
  };

  private addNewAgency = () => this.redirectWithScroll(CONTACT_NEW_FULL_PATH);

  private tryCloseForm = () => {
    const { selectedAgency } = this.state;

    if (selectedAgency) {
      const settings = getCancelDialogPopup({
        continueCanceling: this.closeForm,
        content: this.state.content.cancelWarningDialog,
      });

      this.props.globalContext.notifyListener(
        GlobalEventTypes.notifyingGlobalAlert,
        settings
      );
    } else {
      this.closeForm();
    }
  };

  private closeForm = () => {
    this.redirectWithScroll(PROFILE_CONTACT_FULL_PATH);
    this.props.globalContext.notifyListener(
      GlobalEventTypes.closeAllGlobalAlert
    );
  };

  private redirectWithScroll = (url: string) => {
    scrollToElement(".a-master-page__hidden-panel", { block: "end" });
    this.props.router.navigate(url);
  };

  private static getTranslation = (translator: Translator) => {
    let content: IAddAgencyPageContent;

    content = translator.compileTranslationObject<IAddAgencyPageContent>(
      IAddAgencyPageContentKeys,
      "common"
    );
    content.formContent =
      translator.compileTranslationObject<IAddAgencyFormContent>(
        IAddAgencyFormContentKeys,
        "form content"
      );
    content.cancelWarningDialog =
      translator.compileTranslationObject<ICancelAddingPopupContent>(
        ICancelAddingPopupContentKeys,
        "cancel warning dialog"
      );

    return content;
  };

  private returnToAgenciesList = () =>
    this.redirectWithScroll(PROFILE_CONTACT_FULL_PATH);
}

export default compose(
  withTranslationF("addAgentPage"),
  withBreadcrumbs,
  withRouter,
  withGlobalContext,
  withLaunchDarklyConsumerHOC()
)(AddAgencyPage);
